/**
 * Notification Event Bus - Synchronizes notification state across all components
 *
 * This event bus ensures that when a notification is received, read, or deleted,
 * all components displaying notifications are immediately updated.
 */

type NotificationEventType =
  | 'notification:new'
  | 'notification:read'
  | 'notification:readAll'
  | 'notification:delete';

interface NotificationNewEvent {
  notification: any;
}

interface NotificationReadEvent {
  notificationId: string;
}

interface NotificationReadAllEvent {
  userId: number;
}

interface NotificationDeleteEvent {
  notificationId: string;
}

type NotificationEventData =
  | NotificationNewEvent
  | NotificationReadEvent
  | NotificationReadAllEvent
  | NotificationDeleteEvent;

type EventListener = (data: NotificationEventData) => void;

class NotificationEventBus {
  private listeners: Map<NotificationEventType, Set<EventListener>> = new Map();

  /**
   * Subscribe to notification events
   */
  on(event: NotificationEventType, callback: EventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from notification events
   */
  off(event: NotificationEventType, callback: EventListener): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit a notification event to all subscribers
   */
  emit(event: NotificationEventType, data: NotificationEventData): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
  }

  /**
   * Emit a new notification event
   */
  emitNew(notification: any): void {
    this.emit('notification:new', { notification });
  }

  /**
   * Emit a notification read event
   */
  emitRead(notificationId: string): void {
    this.emit('notification:read', { notificationId });
  }

  /**
   * Emit a read all notifications event
   */
  emitReadAll(userId: number): void {
    this.emit('notification:readAll', { userId });
  }

  /**
   * Emit a notification delete event
   */
  emitDelete(notificationId: string): void {
    this.emit('notification:delete', { notificationId });
  }

  /**
   * Clear all listeners (useful for cleanup/testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

// Export singleton instance
export const notificationEventBus = new NotificationEventBus();

// Export types for use in components
export type {
  NotificationEventType,
  NotificationNewEvent,
  NotificationReadEvent,
  NotificationReadAllEvent,
  NotificationDeleteEvent,
  NotificationEventData,
  EventListener
};
